import { Alert, Button, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { usePrescription } from "src/context/PrescriptionProvider";
import WhiteCard from "../WhiteCard";
import format from "date-fns/format";
import { useManageModal } from "src/hooks/useManageModal";
import PrescribeModal from "../Modals/PrescribeModal2";
import StepperProvider from "src/context/StepperProvider";
import { useUpdatePrescriptionMutation } from "src/api/services/prescriptionsApi";
import { useTitration } from "src/context/TitrationProvider/useTitration";

export type MedicationType = {
  pharmacyId: number;
  pharmacyName: string;
  medicationName: string;
  dosage: string | null;
  id: number;
  dispense_unit: string | null;
  dispense_quantity: number;
  strength: string;
  is_glp1: boolean;
};

interface Props {
  onSuccess: () => Promise<void>;
  patientRegion: string | null;
}

const PrescriptionRequest = ({ onSuccess, patientRegion }: Props) => {
  const [open, { handleClose, handleOpen }] = useManageModal();
  const [updatePrescription] = useUpdatePrescriptionMutation();
  const prescriptionId = usePrescription((prescription) => prescription.id);
  const quantity = usePrescription((p) => p.dispense_quantity);
  const currentTier = useTitration();
  const requestedDosage = usePrescription(
    (prescription) => (prescription.metadata as Record<string, string>)?.dosage,
  );
  const productName = usePrescription(
    (prescription) => prescription.product?.name,
  );

  const isCreated = usePrescription(
    (prescription) => prescription.status === "created",
  );
  const isDenied = usePrescription(
    (prescription) => prescription.status === "denied",
  );
  const prescribedAt = usePrescription(
    (prescription) => prescription.prescribed_at,
  );

  const lastUpdatedBy = usePrescription(
    (prescription) =>
      prescription.provider?.profile.first_name +
      " " +
      prescription.provider?.profile.last_name,
  );

  const daysSupply = usePrescription(
    (prescription) => prescription.days_supply,
  );

  const medicationInterval = useMemo(() => {
    if (daysSupply == null) return "";

    return daysSupply === 84 ? ", Quarterly supply" : ", Monthly supply";
  }, [daysSupply]);

  const alert = useMemo(() => {
    if (quantity != null) {
      return (
        <Alert severity="warning">{`Patient request ${quantity} pills`}</Alert>
      );
    }

    if (requestedDosage != null) {
      return (
        <Alert severity="warning">
          <Typography>{`Patient has requested a max dose of ${requestedDosage}${medicationInterval}`}</Typography>
        </Alert>
      );
    }

    if (currentTier != null) {
      return (
        <Alert severity="warning">
          <Typography>{`Patient NEXT MAX dose is ${currentTier.max_dose} ${currentTier.dose_unit}${medicationInterval}`}</Typography>
        </Alert>
      );
    }

    return null;
  }, [currentTier, medicationInterval, quantity, requestedDosage]);

  const handleUpdateStatus = useCallback(
    async (status: string) => {
      await updatePrescription({
        status: status,
        id: prescriptionId,
      });

      if (status === "denied") {
        onSuccess();
      }
    },
    [onSuccess, prescriptionId, updatePrescription],
  );

  return (
    <>
      <WhiteCard
        width="100%"
        sx={{ background: "white" }}
        direction="column"
        gap={1}
        justifyContent="space-between"
      >
        <Stack gap={1}>
          {alert}
          <Typography fontWeight={600}>
            Medication: {productName}
            {medicationInterval}
          </Typography>
          {prescribedAt != null ? (
            <Typography>
              Prescribed on{" "}
              {format(new Date(prescribedAt), `MMM d yyyy 'at' h:mm a`)} by{" "}
              {lastUpdatedBy}
            </Typography>
          ) : null}
          {isDenied ? <Typography>Denied by {lastUpdatedBy}</Typography> : null}
        </Stack>
        <Stack gap={1} flexWrap="wrap" direction="row">
          <Button disabled={!isCreated} onClick={handleOpen}>
            Prescribe
          </Button>
          <Button
            onClick={() => handleUpdateStatus("denied")}
            variant="secondary"
            disabled={!isCreated}
          >
            Deny
          </Button>
          <Button
            onClick={() => handleUpdateStatus("created")}
            variant="alternate"
            disabled={isCreated || !!prescribedAt}
          >
            Requested
          </Button>
        </Stack>
      </WhiteCard>
      <StepperProvider>
        <PrescribeModal
          onSuccess={onSuccess}
          onClose={handleClose}
          open={open}
          patientRegion={patientRegion}
        />
      </StepperProvider>
    </>
  );
};

export default PrescriptionRequest;
